// src/components/ecommerce/ServiceForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  X,
  Save,
  Upload,
  Trash2,
  Plus,
  Image as ImageIcon,
  Users,
  DollarSign,
  Tag,
  Info,
  Star,
  Globe,
  Clock,
  AlertCircle,
  Edit,
  SlidersHorizontal,
  CheckCircle,
  Loader2,
  FileText,
  MapPin,
  Calendar,
  ChevronUp,
  ChevronDown,
  Award
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { firestoreService } from '../../services/firestoreService';
import { FileUpload } from '../common/FileUpload';

export const ServiceForm = ({ 
  service, 
  storeId, 
  storeData,
  storeTypeConfig,
  categories, 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    category: '',
    subcategory: '',
    tags: [],
    images: [],
    status: 'active',
    featured: false,
    features: [],
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    // Service-specific fields
    duration: '',
    bookingInstructions: '',
    serviceProcess: '',
    deliveryMethod: '',
    requirements: [],
    options: [],
    optionGroups: [] 
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newRequirement, setNewRequirement] = useState('');

  const [uploadStates, setUploadStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [showOptionForm, setShowOptionForm] = useState(false);
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [optionFormData, setOptionFormData] = useState({
    name: '',
    description: '',
    price: 0,
    isFree: false,
    isRequired: false,
    maxQuantity: 1,
    allowMultiple: false
  });
  const [showOptionGroupForm, setShowOptionGroupForm] = useState(false);
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);
  const [optionGroupFormData, setOptionGroupFormData] = useState({
    name: '',
    description: '',
    allFree: false,
    freeQuantity: 0,
    isRequired: false,
    allowMultipleSelections: true,
    options: []
  });

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || '',
        description: service.description || '',
        price: service.price?.toString() || '',
        sku: service.sku || '',
        category: service.category || '',
        subcategory: service.subcategory || '',
        tags: service.tags || [],
        images: service.images || [],
        status: service.status || 'active',
        featured: service.featured || false,
        features: service.features || [],
        seoTitle: service.seoTitle || '',
        seoDescription: service.seoDescription || '',
        seoKeywords: service.seoKeywords || [],
        duration: service.duration || '',
        bookingInstructions: service.bookingInstructions || '',
        serviceProcess: service.serviceProcess || '',
        deliveryMethod: service.deliveryMethod || '',
        requirements: service.requirements || [],
        options: service.options || [],
        optionGroups: service.optionGroups || []
      });
    } else {
      // Generate SKU for new service
      const sku = `SVC-${Date.now().toString().slice(-6)}`;
      setFormData(prev => ({ ...prev, sku }));
    }
  }, [service]);

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const handleImageUpload = async (files) => {
    setUploadStates(prev => ({ ...prev, images: { uploading: true, error: null } }));
    
    try {
      const fileArray = Array.isArray(files) ? files : [files];
      
      const uploadPromises = fileArray.map(file => 
        firestoreService.uploadFile(file, `pb-${storeId}/services/images`)
      );
      
      const results = await Promise.all(uploadPromises);
      const imageUrls = results.map(result => result.url);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageUrls]
      }));

      setUploadStates(prev => ({ ...prev, images: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error('Error uploading service images:', error);
      setUploadStates(prev => ({ 
        ...prev, 
        images: { uploading: false, error: error.message } 
      }));
      setValidationErrors(prev => ({ ...prev, images: error.message }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Service name is required';
    if (!formData.description.trim()) newErrors.description = 'Service description is required';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.bookingInstructions.trim()) {
      newErrors.bookingInstructions = 'Booking instructions are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const serviceData = {
        ...formData,
        price: parseFloat(formData.price),
      };

      if (service) {
        await ecommerceService.updateProduct(storeId, service.id, serviceData);
      } else {
        await ecommerceService.addProduct(storeId, serviceData);
      }

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOption = () => {
    setEditingOptionIndex(null);
    setOptionFormData({
      name: '',
      description: '',
      price: 0,
      isFree: false,
      isRequired: false,
      maxQuantity: 1,
      allowMultiple: false
    });
    setShowOptionForm(true);
  };

  const handleSaveOption = () => {
    if (!optionFormData.name.trim()) {
      alert('Option name is required');
      return;
    }

    // Saving to a specific group
    if (editingGroupIndex !== null) {
      const newGroups = [...formData.optionGroups];
      if (!newGroups[editingGroupIndex].options) {
        newGroups[editingGroupIndex].options = [];
      }

      if (editingOptionIndex !== null) {
        // Editing existing option
        newGroups[editingGroupIndex].options[editingOptionIndex] = optionFormData;
      } else {
        // Adding new option
        newGroups[editingGroupIndex].options.push(optionFormData);
      }

      setFormData(prev => ({ ...prev, optionGroups: newGroups }));
    }

    setShowOptionForm(false);
    setEditingGroupIndex(null);
    setEditingOptionIndex(null);
  };

  const handleAddOptionGroup = () => {
    setEditingGroupIndex(null);
    setOptionGroupFormData({
      name: '',
      description: '',
      allFree: false,
      freeQuantity: 0,
      isRequired: false,
      allowMultipleSelections: true,
      options: []
    });
    setShowOptionGroupForm(true);
  };

  const handleEditOptionGroup = (index) => {
    setEditingGroupIndex(index);
    setOptionGroupFormData(formData.optionGroups[index]);
    setShowOptionGroupForm(true);
  };

  const handleSaveOptionGroup = () => {
    if (!optionGroupFormData.name.trim()) {
      alert('Group name is required');
      return;
    }

    const newGroups = [...(formData.optionGroups || [])];
    if (editingGroupIndex !== null) {
      newGroups[editingGroupIndex] = optionGroupFormData;
    } else {
      newGroups.push(optionGroupFormData);
    }

    setFormData(prev => ({ ...prev, optionGroups: newGroups }));
    setShowOptionGroupForm(false);
    setEditingGroupIndex(null);
  };

  const handleDeleteOptionGroup = (index) => {
    if (window.confirm(`Delete "${formData.optionGroups[index].name}" group and all its options?`)) {
      setFormData(prev => ({
        ...prev,
        optionGroups: prev.optionGroups.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAddOptionToGroup = (groupIndex) => {
    setEditingGroupIndex(groupIndex);
    setEditingOptionIndex(null);
    setOptionFormData({
      name: '',
      description: '',
      price: 0,
      isRequired: false,
      maxQuantity: 1,
      allowMultiple: false
    });
    setShowOptionForm(true);
  };

  const handleEditOption = (groupIndex, optionIndex) => {
    setEditingGroupIndex(groupIndex);
    setEditingOptionIndex(optionIndex);
    setOptionFormData(formData.optionGroups[groupIndex].options[optionIndex]);
    setShowOptionForm(true);
  };

  const handleDeleteOption = (groupIndex, optionIndex) => {
    if (window.confirm('Delete this option?')) {
      const newGroups = [...formData.optionGroups];
      newGroups[groupIndex].options = newGroups[groupIndex].options.filter((_, i) => i !== optionIndex);
      setFormData(prev => ({ ...prev, optionGroups: newGroups }));
    }
  };

  const handleMoveOption = (index, direction) => {
    const newOptions = [...formData.options];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newOptions.length) return;
    
    [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Info },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'booking', label: 'Booking Details', icon: Calendar },
    { id: 'options', label: 'Service Options', icon: SlidersHorizontal },
    { id: 'features', label: 'Features', icon: Award },
    { id: 'seo', label: 'SEO', icon: Globe }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {service ? 'Edit Service' : 'Add New Service'}
            </h2>
            <p className="text-gray-600">
              {service ? 'Update service information' : 'Create a new service offering'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                  {errors[tab.id] && (
                    <AlertCircle className="h-3 w-3 text-red-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Deep Tissue Massage, Website Design, Photography Session"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => handleInputChange('sku', e.target.value)}
                    placeholder="Auto-generated if empty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.category ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category && <p className="text-red-600 text-sm mt-1">{errors.category}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Description *
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={6}
                  placeholder="Provide a detailed description of this service, what's included, expected outcomes, and what makes it unique..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <p className="text-sm text-gray-500 mt-1">
                  💡 Be detailed and clear about what this service includes and what customers can expect.
                </p>
                {errors.description && (
                  <p className="text-red-600 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium">Featured Service</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm font-medium">Status:</span>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Service Images</h3>
                <FileUpload
                  onFileSelect={handleImageUpload}
                  accept="image/*"
                  multiple
                  className="mb-4"
                />
                
                {uploadStates.images?.uploading && (
                  <div className="flex items-center gap-2 text-blue-600 mb-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Uploading images...</span>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Service ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Price *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.price ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.price && <p className="text-red-600 text-sm mt-1">{errors.price}</p>}
                <p className="text-sm text-gray-500 mt-1">
                  Price per session or service delivery
                </p>
              </div>
            </div>
          )}

          {/* OPTIONS TAB - Enhanced with Option Groups */}
          {activeTab === 'options' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">About Option Groups</h4>
                <p className="text-sm text-blue-700">
                  Create custom option groups (e.g., "Flavours", "Extras", "Sides") with flexible pricing. 
                  You can set how many options are free/complimentary before charging.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">Option Groups</h4>
                  <p className="text-sm text-gray-600">Organize options into named groups</p>
                </div>
                <button
                  onClick={handleAddOptionGroup}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Option Group
                </button>
              </div>

              {formData.optionGroups && formData.optionGroups.length > 0 ? (
                <div className="space-y-4">
                  {formData.optionGroups.map((group, groupIndex) => (
                    <div
                      key={groupIndex}
                      className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-colors"
                    >
                      {/* Group Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{group.name}</h3>
                            {group.allFree && (
                              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                                All Complimentary
                              </span>
                            )}
                            {!group.allFree && group.freeQuantity > 0 && (
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                                First {group.freeQuantity} Free
                              </span>
                            )}
                          </div>
                          {group.description && (
                            <p className="text-sm text-gray-600">{group.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleEditOptionGroup(groupIndex)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit group"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteOptionGroup(groupIndex)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete group"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Options in Group */}
                      <div className="space-y-2 ml-4 border-l-2 border-gray-200 pl-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">
                            Options ({group.options?.length || 0})
                          </span>
                          <button
                            onClick={() => handleAddOptionToGroup(groupIndex)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                            Add Option
                          </button>
                        </div>

                        {group.options && group.options.length > 0 ? (
                          group.options.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">{option.name}</span>
                                    {option.isRequired && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                                        Required
                                      </span>
                                    )}
                                  </div>
                                  {option.description && (
                                    <p className="text-xs text-gray-600 mb-2">{option.description}</p>
                                  )}
                                  <div className="flex items-center gap-3 text-xs text-gray-500">
                                    <span className="font-semibold text-gray-900">
                                      R{parseFloat(option.price).toFixed(2)}
                                    </span>
                                    {option.allowMultiple && (
                                      <span>• Max: {option.maxQuantity}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-3">
                                  <button
                                    onClick={() => handleEditOption(groupIndex, optionIndex)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Edit"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteOption(groupIndex, optionIndex)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-6 text-gray-500 text-sm">
                            No options added yet
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                  <SlidersHorizontal className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No option groups yet</h4>
                  <p className="text-gray-600 mb-4">
                    Create groups like "Flavours", "Extras", or "Sides" to organize your options
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Booking Details Tab */}
          {activeTab === 'booking' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  How to Book This Service *
                </label>
                <textarea
                  value={formData.bookingInstructions || ''}
                  onChange={(e) => handleInputChange('bookingInstructions', e.target.value)}
                  rows={4}
                  placeholder="Provide clear step-by-step instructions on how customers can book or purchase this service. Include contact information, scheduling details, payment terms, or next steps."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.bookingInstructions ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.bookingInstructions && (
                  <p className="text-red-600 text-sm mt-1">{errors.bookingInstructions}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Service Duration
                  </label>
                  <input
                    type="text"
                    value={formData.duration || ''}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="e.g., 60 minutes, 2 hours, 1 week"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Service Location
                  </label>
                  <select
                    value={formData.deliveryMethod || ''}
                    onChange={(e) => handleInputChange('deliveryMethod', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select location type</option>
                    <option value="in-person">In-Person</option>
                    <option value="remote">Remote/Online</option>
                    <option value="hybrid">Hybrid (In-Person & Remote)</option>
                    <option value="on-site">On-Site (At Customer Location)</option>
                    <option value="scheduled">Scheduled Appointment</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Process/Steps
                </label>
                <textarea
                  value={formData.serviceProcess || ''}
                  onChange={(e) => handleInputChange('serviceProcess', e.target.value)}
                  rows={4}
                  placeholder="Describe the step-by-step process of how this service is delivered..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Customer Requirements
                </label>
                <div className="space-y-2">
                  {formData.requirements?.map((req, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={req}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                      <button
                        onClick={() => removeRequirement(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addRequirement()}
                      placeholder="What customers need to provide or prepare"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={addRequirement}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Features & Benefits
                </label>
                <p className="text-sm text-gray-500 mb-4">
                  List key features, benefits, or what's included in this service
                </p>
                <div className="space-y-2">
                  {formData.features?.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      />
                      <button
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                      placeholder="e.g., Professional consultation included"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                      onClick={addFeature}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags?.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:bg-blue-200 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    placeholder="Add tags (e.g., relaxation, wellness, beauty)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">SEO Title</label>
                <input
                  type="text"
                  value={formData.seoTitle}
                  onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                  placeholder="Service name - Your Business Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  maxLength={60}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.seoTitle.length}/60 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">SEO Description</label>
                <textarea
                  value={formData.seoDescription}
                  onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                  rows={3}
                  placeholder="Brief description for search engines..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  maxLength={160}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.seoDescription.length}/160 characters
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Service
              </>
            )}
          </button>
        </div>
      </div>

      {/* ========== OPTION GROUP FORM MODAL ========== */}
      {showOptionGroupForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
              <h3 className="text-lg font-bold text-gray-900">
                {editingGroupIndex !== null ? 'Edit Option Group' : 'Add Option Group'}
              </h3>
              <button
                onClick={() => {
                  setShowOptionGroupForm(false);
                  setEditingGroupIndex(null);
                }}
                className="p-2 hover:bg-white rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={optionGroupFormData.name}
                  onChange={(e) => setOptionGroupFormData(prev => ({ 
                    ...prev, 
                    name: e.target.value 
                  }))}
                  placeholder="e.g., Flavours, Extras, Sides, Toppings"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This name will be shown to customers
                </p>
              </div>

              {/* Group Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={optionGroupFormData.description}
                  onChange={(e) => setOptionGroupFormData(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                  rows={2}
                  placeholder="Optional description for this option group..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Pricing Configuration */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-5 space-y-4">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Pricing Configuration
                </h4>

                {/* All Free Toggle */}
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-white rounded-lg border border-blue-200 hover:shadow-sm transition-shadow">
                  <input
                    type="checkbox"
                    checked={optionGroupFormData.allFree}
                    onChange={(e) => setOptionGroupFormData(prev => ({ 
                      ...prev, 
                      allFree: e.target.checked,
                      freeQuantity: e.target.checked ? 0 : prev.freeQuantity
                    }))}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">All Options Complimentary</span>
                    <p className="text-sm text-gray-600 mt-0.5">
                      All options in this group are free - no charges apply regardless of quantity
                    </p>
                  </div>
                </label>

                {/* Free Quantity */}
                {!optionGroupFormData.allFree && (
                  <div className="p-4 bg-white rounded-lg border border-blue-200">
                    <label className="block text-sm font-medium mb-3 text-gray-900">
                      Number of Free/Complimentary Options
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={optionGroupFormData.freeQuantity}
                        onChange={(e) => setOptionGroupFormData(prev => ({ 
                          ...prev, 
                          freeQuantity: Math.max(0, parseInt(e.target.value) || 0)
                        }))}
                        min="0"
                        className="w-32 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center font-semibold text-lg"
                      />
                      <span className="text-sm text-gray-600">options are free</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                      Customers can select this many options at no extra charge. 
                      Additional options will be charged at their individual prices.
                    </p>
                    
                    {/* Example Calculation */}
                    {optionGroupFormData.freeQuantity > 0 && (
                      <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                        <p className="text-xs font-semibold text-blue-900 mb-2">📊 Pricing Example:</p>
                        <div className="space-y-1 text-xs text-blue-800">
                          <p>
                            • First {optionGroupFormData.freeQuantity} {optionGroupFormData.freeQuantity === 1 ? 'option' : 'options'}: 
                            <span className="ml-1 font-bold text-green-600">R0.00</span>
                          </p>
                          <p>
                            • Option #{optionGroupFormData.freeQuantity + 1} onwards: 
                            <span className="ml-1 font-bold text-gray-900">charged at option price</span>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selection Behavior */}
              <div className="space-y-3 border-t pt-4">
                <h4 className="font-semibold text-gray-900">Selection Behavior</h4>
                
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={optionGroupFormData.isRequired}
                    onChange={(e) => setOptionGroupFormData(prev => ({ 
                      ...prev, 
                      isRequired: e.target.checked
                    }))}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">Required Group</span>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Customer must select at least one option from this group
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={optionGroupFormData.allowMultipleSelections}
                    onChange={(e) => setOptionGroupFormData(prev => ({ 
                      ...prev, 
                      allowMultipleSelections: e.target.checked
                    }))}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">Allow Multiple Selections</span>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Customer can select multiple options from this group
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowOptionGroupForm(false);
                  setEditingGroupIndex(null);
                }}
                className="px-6 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOptionGroup}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md hover:shadow-lg"
              >
                <Save className="h-4 w-4" />
                {editingGroupIndex !== null ? 'Update' : 'Create'} Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== OPTION FORM MODAL ========== */}
      {showOptionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-purple-50 to-pink-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {editingOptionIndex !== null ? 'Edit Option' : 'Add Option'}
                </h3>
                {editingGroupIndex !== null && formData.optionGroups[editingGroupIndex] && (
                  <p className="text-sm text-gray-600 mt-1">
                    Group: <span className="font-semibold">{formData.optionGroups[editingGroupIndex].name}</span>
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowOptionForm(false);
                  setEditingOptionIndex(null);
                  setEditingGroupIndex(null);
                }}
                className="p-2 hover:bg-white rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Pricing Info Banner */}
              {editingGroupIndex !== null && formData.optionGroups[editingGroupIndex] && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Group Pricing Rules
                  </h4>
                  {formData.optionGroups[editingGroupIndex].allFree ? (
                    <p className="text-sm text-blue-700">
                      ✓ All options in this group are complimentary (no charges)
                    </p>
                  ) : formData.optionGroups[editingGroupIndex].freeQuantity > 0 ? (
                    <div className="text-sm text-blue-700 space-y-1">
                      <p>• First {formData.optionGroups[editingGroupIndex].freeQuantity} options: <strong>Free</strong></p>
                      <p>• Additional options: <strong>Charged at price below</strong></p>
                    </div>
                  ) : (
                    <p className="text-sm text-blue-700">
                      All options in this group are charged at their individual prices
                    </p>
                  )}
                </div>
              )}

              {/* Option Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Option Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={optionFormData.name}
                  onChange={(e) => setOptionFormData(prev => ({ 
                    ...prev, 
                    name: e.target.value 
                  }))}
                  placeholder="e.g., Vanilla, Extra Cheese, Large Size"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Option Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={optionFormData.description}
                  onChange={(e) => setOptionFormData(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                  rows={2}
                  placeholder="Brief description of this option..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Price per Unit (R) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 font-semibold text-lg">
                    R
                  </span>
                  <input
                    type="number"
                    value={optionFormData.price}
                    onChange={(e) => setOptionFormData(prev => ({ 
                      ...prev, 
                      price: parseFloat(e.target.value) || 0
                    }))}
                    step="0.01"
                    min="0"
                    className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  This price applies to each unit after free allocation (if any)
                </p>
              </div>

              {/* Quantity Settings */}
              <div className="space-y-4 border-t pt-5">
                <h4 className="font-semibold text-gray-900">Quantity Settings</h4>
                
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={optionFormData.allowMultiple}
                    onChange={(e) => setOptionFormData(prev => ({ 
                      ...prev, 
                      allowMultiple: e.target.checked 
                    }))}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">Allow Multiple Quantities</span>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Customer can select multiple of this option
                    </p>
                  </div>
                </label>

                {optionFormData.allowMultiple && (
                  <div className="ml-8 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="block text-sm font-medium mb-2 text-gray-900">
                      Maximum Quantity Allowed
                    </label>
                    <input
                      type="number"
                      value={optionFormData.maxQuantity}
                      onChange={(e) => setOptionFormData(prev => ({ 
                        ...prev, 
                        maxQuantity: Math.max(1, parseInt(e.target.value) || 1)
                      }))}
                      min="1"
                      className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center font-semibold"
                    />
                    <p className="text-xs text-gray-600 mt-2">
                      Maximum number of this option per order
                    </p>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={optionFormData.isRequired}
                    onChange={(e) => setOptionFormData(prev => ({ 
                      ...prev, 
                      isRequired: e.target.checked
                    }))}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">Required Option</span>
                    <p className="text-sm text-gray-600 mt-0.5">
                      Customer must select this specific option
                    </p>
                  </div>
                </label>
              </div>

              {/* Price Preview */}
              {editingGroupIndex !== null && formData.optionGroups[editingGroupIndex] && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-300 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-gray-600" />
                    Pricing Preview
                  </h4>
                  <div className="space-y-2 text-sm">
                    {formData.optionGroups[editingGroupIndex].allFree ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <p className="text-green-700 font-medium">
                          This option will always be complimentary
                        </p>
                      </div>
                    ) : formData.optionGroups[editingGroupIndex].freeQuantity > 0 ? (
                      <div className="space-y-3">
                        <div className="p-3 bg-white rounded border border-gray-200">
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium">If customer selects ≤{formData.optionGroups[editingGroupIndex].freeQuantity} total options:</span>
                          </p>
                          <p className="text-2xl font-bold text-green-600">R0.00</p>
                        </div>
                        <div className="p-3 bg-white rounded border border-gray-200">
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium">Each option beyond {formData.optionGroups[editingGroupIndex].freeQuantity}:</span>
                          </p>
                          <p className="text-2xl font-bold text-gray-900">+R{parseFloat(optionFormData.price).toFixed(2)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-white rounded border border-gray-200">
                        <p className="text-gray-700 mb-1">
                          <span className="font-medium">Each selection:</span>
                        </p>
                        <p className="text-2xl font-bold text-gray-900">+R{parseFloat(optionFormData.price).toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowOptionForm(false);
                  setEditingOptionIndex(null);
                  setEditingGroupIndex(null);
                }}
                className="px-6 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOption}
                className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-md hover:shadow-lg"
              >
                <Save className="h-4 w-4" />
                {editingOptionIndex !== null ? 'Update' : 'Add'} Option
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};